import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createOriginDestiny, updateOriginDestiny } from '@/api/originDestinyAPI'
import { toast } from 'react-toastify'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline'

interface OriginDestinyManagerProps {
  type: 'origin' | 'destiny'
  onSelect: (item: { id: number; name: string }) => void
  selectedValue?: { id: number; name: string }
}

export default function OriginDestinyManager({ type, onSelect, selectedValue }: OriginDestinyManagerProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    countryId: 10 // Default country ID
  })
  
  const queryClient = useQueryClient()

  const createMutation = useMutation({
    mutationFn: createOriginDestiny,
    onSuccess: (data) => {
      toast.success(`${type === 'origin' ? 'Origen' : 'Destino'} creado exitosamente`)
      queryClient.invalidateQueries({ queryKey: ['origin_destiny'] })
      onSelect({ id: data.id, name: data.name })
      setIsOpen(false)
      setFormData({ name: '', description: '', countryId: 10 })
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || `Error al crear ${type === 'origin' ? 'origen' : 'destino'}`)
    }
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => updateOriginDestiny(id, data),
    onSuccess: (data) => {
      toast.success(`${type === 'origin' ? 'Origen' : 'Destino'} actualizado exitosamente`)
      queryClient.invalidateQueries({ queryKey: ['origin_destiny'] })
      onSelect({ id: data.id, name: data.name })
      setIsOpen(false)
      setFormData({ name: '', description: '', countryId: 10 })
      setIsEditing(false)
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || `Error al actualizar ${type === 'origin' ? 'origen' : 'destino'}`)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (isEditing && selectedValue) {
      updateMutation.mutate({
        id: selectedValue.id,
        data: {
          name: formData.name,
          description: formData.description
        }
      })
    } else {
      createMutation.mutate({
        name: formData.name,
        countryId: formData.countryId
      })
    }
  }

  const handleEdit = () => {
    if (selectedValue) {
      setFormData({
        name: selectedValue.name,
        description: '',
        countryId: 10
      })
      setIsEditing(true)
      setIsOpen(true)
    }
  }

  const handleCreate = () => {
    setFormData({ name: '', description: '', countryId: 10 })
    setIsEditing(false)
    setIsOpen(true)
  }

  return (
    <div className="flex items-center gap-2">
      {/* No mostrar el nombre del origen/destino seleccionado */}
      
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            onClick={handleCreate}
            className="h-10 w-10 p-0"
          >
            <PlusIcon className="h-4 w-4" />
          </Button>
        </DialogTrigger>
        {/* No mostrar botón de editar */}
        
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {`Crear ${type === 'origin' ? 'Origen' : 'Destino'}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={`Nombre del ${type === 'origin' ? 'origen' : 'destino'}`}
                required
              />
            </div>
            {/* No mostrar campo de descripción ni edición */}
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {createMutation.isPending ? 'Guardando...' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
} 